class BookingCalendar extends HTMLElement {
  connectedCallback() {
    const json = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.services = json.services;

    this.settings = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? this.dataset.timezone,
      maxMonthsFuture: 12,
      maxMonthsPast: 0,
      translations: json.translations,
      storefrontToken: this.dataset.storefrontToken,
      storeDomain: this.dataset.storeDomain,
      currency: json.currency
    };

    if (!this.dayTiles.length || !this.services.length) return;

    this.templateTile = this.dayTiles[0].cloneNode(true);
    this.templateTimeTile = this.querySelector('[data-calendar-time-tile]').cloneNode(true);
    this.today = this.getNowInTimezone();
    this.currentMonthDate = new Date(this.today.year, this.today.month, 1);

    this.renderWeekedays();

    if (this.requiresServiceSelection) {
      this.showServiceSelection();
    } else {
      this.querySelector('[data-services-back]').style.display = 'none';
      this.selectedService = this.services[0];
      this.selectedVariant = this.selectedService.product.variants[0];
      this.selectedServiceLabelElement.textContent = this.selectedServiceLabel;
      this.selectedVariantPriceElement.textContent = this.selectedVariantPrice;
      this.renderCalendar().then(() => {
        this.selectedDate = this.getInitialSelectedDate();
      });
      this.showCalendar();
    }

    this.grid.addEventListener('click', this.handleSelectGridDay.bind(this));
    this.prevButton?.addEventListener('click', this.handleClickPrevMonth.bind(this));
    this.nextButton?.addEventListener('click', this.handleClickNextMonth.bind(this));
    this.querySelectorAll('[data-calendar-service-tile]').forEach(tile => {
      tile.addEventListener('click', this.handleSelectService.bind(this));
    });
    this.querySelector('[data-services-back]')?.addEventListener('click', this.showServiceSelection.bind(this));
    this.querySelector('[data-calendar-back]')?.addEventListener('click', this.showCalendar.bind(this));
    this.checkoutButton?.addEventListener('click', this.handleCheckout.bind(this));
  }

  // — Getters: DOM —

  get grid() {
    return this.querySelector('[data-events-calendar-grid]');
  }

  get monthLabel() {
    return this.querySelector('[data-events-calendar-month]');
  }

  get prevButton() {
    return this.querySelector('[data-events-calendar-prev]');
  }

  get nextButton() {
    return this.querySelector('[data-events-calendar-next]');
  }

  get weekdayElements() {
    return [...this.querySelectorAll('[data-events-calendar-weekday]')];
  }

  get dayTiles() {
    return [...this.querySelectorAll('[data-events-calendar-tile]')];
  }

  get calendarView() {
    return this.querySelector('[data-calendar-date-select]');
  }

  get timesView() {
    return this.querySelector('[data-times-view]');
  }

  get servicesView() {
    return this.querySelector('[data-calendar-service-select]');
  }

  get timesLabel() {
    return this.querySelector('[data-calendar-times-label]');
  }

  get servicesLabel() {
    return this.querySelector('[data-calendar-services-label]');
  }

  get timesContainer() {
    return this.querySelector('[data-calendar-times]');
  }

  get checkoutButton() {
    return this.querySelector('[data-booking-checkout]');
  }

  get selectedServiceLabelElement() {
    return this.querySelector('[data-selected-service]');
  }

  get selectedVariantPriceElement() {
    return this.querySelector('[data-selected-price]');
  }

  get selectedDateElement() {
    return this.querySelector('[data-selected-date]');
  }

  get bookingDetails() {
    return this.querySelector('[data-booking-details]');
  }

  get bookingConfiguration() {
    return this.querySelector('[data-booking-configuration]');
  }

  // — Getters: State —

  get servicesByWeekday() {
    return this.getWeekdayOrder().map(day =>
      this.selectedService.availability.filter(d => d.day_of_week.toLowerCase() === day.toLowerCase())
    );
  }

  get hasMultipleServices() {
    return this.services.length > 1;
  }

  get hasMultipleVariants() {
    return this.services.some(service => service.product.variants.length > 1);
  }

  get requiresServiceSelection() {
    return this.hasMultipleServices || this.hasMultipleVariants;
  }

  get selectedServiceLabel() {
    const { product } = this.selectedService;
    return product.variants.length > 1
      ? `${product.title} - ${this.selectedVariant.title}`
      : product.title;
  }

  get selectedVariantPrice() {
    if (this.selectedVariant.price === 0) return this.settings.translations.free;

    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: this.settings.currency
    }).format(this.selectedVariant.price / 100);
  }

  get selectedTimeLabel() {
    const [hour, minute] = this.selectedTime.split(':').map(Number);
    const period = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const time = `${hour12}:${String(minute).padStart(2, '0')}${period}`;
    return `${this.formatDateLabel(this.selectedDate)} at ${time}`;
  }

  // — Event Handlers —

  handleSelectService(e) {
    e.preventDefault();
    const tile = e.target.closest('[data-calendar-service-tile]');
    this.selectService(tile.dataset.serviceId, tile.dataset.variantId);
  }

  handleSelectGridDay(e) {
    e.preventDefault();
    const tile = e.target.closest('[data-events-calendar-date]');
    if (!tile || tile.dataset.state !== 'filled') return;

    this.querySelector('[data-events-calendar-tile][data-selected]')?.removeAttribute('data-selected');
    tile.setAttribute('data-selected', 'true');
    this.selectedDate = tile.dataset.eventsCalendarDate;
    this.selectedDateElement.textContent = this.formatDateLabel(this.selectedDate);

    this.showTimeSelection();
  }

  handleClickNextMonth(e) {
    e.preventDefault();
    this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
    this.renderCalendar();
  }

  handleClickPrevMonth(e) {
    e.preventDefault();
    this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
    this.renderCalendar();
  }

  handleSelectTime(slot, tile) {
    this.selectedTime = slot;
    this.timesContainer.querySelectorAll('[data-calendar-time-tile]').forEach(t => t.removeAttribute('data-selected'));
    tile.setAttribute('data-selected', 'true');
    this.selectedDateElement.textContent = this.selectedTimeLabel;
    this.showCheckoutButton();
  }

  handleCheckout() {
    if (!this.selectedService || !this.selectedDate || !this.selectedTime) return;

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart/add';

    const fields = {
      'id': this.selectedVariant.id,
      'quantity': 1,
      'return_to': '/checkout',
      'properties[booking_service]': this.selectedServiceLabel,
      'properties[booking_date]': this.selectedDate,
      'properties[booking_time]': this.selectedTime,
      'properties[booking_timezone]': this.settings.timezone
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  // — Actions —

  showCheckoutButton() {
    this.checkoutButton.style.opacity = 1;
    this.checkoutButton.style.visibility = 'visible';
    this.checkoutButton.removeAttribute('disabled');

    const rect = this.bookingDetails.getBoundingClientRect();
    const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!isInView) {
      const offset = this.bookingDetails.getBoundingClientRect().top + window.scrollY - 48
      this.bookingDetails.scrollIntoView({ top: offset, behavior: 'smooth' });
    }
  }

  hideCheckoutButton() {
    this.checkoutButton.style.opacity = 0;
    this.checkoutButton.style.visibility = 'invisible';
    this.checkoutButton.setAttribute('disabled', true);
    // const offset = this.bookingConfiguration.getBoundingClientRect().top + window.scrollY - 48
    // this.bookingConfiguration.scrollIntoView({ top: offset, behavior: 'smooth' });
  }

  selectService(gid, variantId) {
    if (gid === this.selectedService?.gid && parseInt(variantId) === parseInt(this.selectedVariant?.id)) return;

    this.selectedService = this.services.find(i => i.gid === gid);
    this.selectedVariant = this.selectedService.product.variants.find(i => parseInt(i.id) === parseInt(variantId));

    this.selectedServiceLabelElement.textContent = this.selectedServiceLabel;
    this.selectedVariantPriceElement.textContent = this.selectedVariantPrice;
    this.servicesLabel.textContent = this.selectedServiceLabel;

    this.showCalendar();
    this.renderCalendar();
  }

  // — Views —

  showServiceSelection() {
    this.timesView.classList.add('hidden');
    this.calendarView.classList.add('hidden');
    this.servicesView.classList.remove('hidden');
    this.selectedDate = null;
    this.selectedTime = null;
    this.hideCheckoutButton();
  }

  showCalendar() {
    this.timesView.classList.add('hidden');
    this.servicesView.classList.add('hidden');
    this.calendarView.classList.remove('hidden');
    this.selectedTime = null;
    this.hideCheckoutButton();
  }

  async showTimeSelection() {
    this.bookedSlots = await this.fetchBookedSlotsForDate(this.selectedDate);
    this.renderTimeSelection();
    this.calendarView.classList.add('hidden');
    this.servicesView.classList.add('hidden');
    this.timesView.classList.remove('hidden');
    this.hideCheckoutButton();
  }

  // — Render —

  renderWeekedays() {
    this.weekdayElements.forEach(weekday => {
      const translation = this.settings.translations[`${weekday.dataset.eventsCalendarWeekday}_short`];
      if (translation) weekday.textContent = translation;
    });
  }

  async renderCalendar() {
    const year = this.currentMonthDate.getFullYear();
    const month = this.currentMonthDate.getMonth();

    this.monthLabel.textContent = `${this.settings.translations[this.getMonthName(month)]} ${year}`;
    this.blackouts = await this.fetchBlackoutsForMonth(year, month);
    this.grid.innerHTML = '';

    const firstDay = new Date(year, month, 1);
    const weekdayOrder = this.getWeekdayOrder();
    const startDay = weekdayOrder.indexOf(this.getWeekdayName(firstDay.getDay()));
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayKey = this.formatDateKey(new Date(this.today.year, this.today.month, this.today.day));

    this.updateButtonStates();

    for (let i = 0; i < 42; i++) {
      const tile = this.templateTile.cloneNode(true);
      const dayElement = tile.querySelector('[data-events-calendar-tile-day]');
      const dayNumber = i - startDay + 1;

      tile.removeAttribute('data-events-calendar-date');

      if (dayNumber < 1 || dayNumber > daysInMonth) {
        tile.setAttribute('data-state', 'hidden');
        dayElement.textContent = '';
        this.grid.appendChild(tile);
        continue;
      }

      const currentDate = new Date(year, month, dayNumber);
      const dateKey = this.formatDateKey(currentDate);
      const hasService = this.selectedService.availability.find(
        i => i.day_of_week.toLowerCase() === this.getWeekdayName(currentDate.getDay()).toLowerCase()
      );
      const isPast = currentDate <= new Date(this.today.year, this.today.month, this.today.day);
      const isBlackedOut = this.isBlackedOut(dateKey);

      tile.setAttribute('data-state', hasService && !isPast && !isBlackedOut ? 'filled' : 'empty');
      tile.setAttribute('data-events-calendar-date', dateKey);
      dayElement.textContent = dayNumber;

      if (dateKey === todayKey) tile.setAttribute('data-today', 'true');
      if (this.selectedDate === dateKey) tile.setAttribute('data-selected', 'true');

      this.grid.appendChild(tile);
    }

    this.querySelector('[data-calendar-date-select]').classList.remove('blur-xs');
  }

  renderTimeSelection() {
    this.timesLabel.textContent = this.formatDateLabel(this.selectedDate);
    this.timesContainer.innerHTML = '';

    this.getTimeSlotsForDate(this.selectedDate).forEach(slot => {
      const tile = this.templateTimeTile.cloneNode(true);
      tile.classList.remove('hidden');
      tile.querySelector('[data-calendar-time-label]').textContent = slot;
      tile.dataset.calendarTime = slot;
      if (this.isSlotTaken(this.selectedDate, slot)) {
        tile.dataset.booked = 'true';
      } else {
        tile.addEventListener('click', () => this.handleSelectTime(slot, tile));
      }
      this.timesContainer.appendChild(tile);
    });
  }

  // — Data —

  async fetchBlackoutsForMonth(year, month) {
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`;

    const query = `{
      metaobjects(type: "booking_blackout", first: 50) {
        edges {
          node {
            fields { key value }
          }
        }
      }
    }`;

    const response = await fetch(`https://${this.settings.storeDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.settings.storefrontToken
      },
      body: JSON.stringify({ query })
    });

    const { data } = await response.json();

    return data.metaobjects.edges
      .map(({ node }) => {
        const fields = {};
        node.fields.forEach(({ key, value }) => fields[key] = value);
        return fields;
      })
      .filter(blackout => blackout.date_start <= endDate && blackout.date_end >= startDate);
  }

  async fetchBookedSlotsForDate(dateKey) {
    const query = `{
      metaobjects(type: "booking_holds", first: 250) {
        edges {
          node {
            fields {
              key
              value
            }
          }
        }
      }
    }`;

    const response = await fetch(`https://${this.settings.storeDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.settings.storefrontToken
      },
      body: JSON.stringify({ query })
    });

    const { data } = await response.json();

    const bookings = data.metaobjects.edges.map(({ node }) => {
      const fields = {};
      node.fields.forEach(({ key, value }) => fields[key] = value);
      return fields;
    });
    return bookings.filter(i => i.date_filter === dateKey);
  }

  isSlotTaken(dateKey, slot) {
    const [slotHour, slotMinute] = slot.split(':').map(Number);
    const slotStart = slotHour * 60 + slotMinute;
    const slotEnd = slotStart + this.selectedService.booking_duration;

    return this.bookedSlots.some(booking => {
      if (booking.date_filter !== dateKey) return false;

      const bookedTime = booking.when.substring(11, 16);
      const [bookedHour, bookedMinute] = bookedTime.split(':').map(Number);
      const bookedStart = bookedHour * 60 + bookedMinute;
      const bookedEnd = bookedStart + this.selectedService.booking_duration;
      const bookedEndWithGap = bookedEnd + this.selectedService.booking_gap;

      // Block if slot overlaps with the booked window OR starts within the gap after it
      return slotStart < bookedEndWithGap && slotEnd > bookedStart;
    });
  }

  isBlackedOut(dateKey) {
    return this.blackouts.some(blackout => {
      const services = JSON.parse(blackout.services ?? '[]');
      const appliesToService = services.length === 0 ||
        services.some(gid => gid === this.selectedService.gid);
      return appliesToService && dateKey >= blackout.date_start && dateKey <= blackout.date_end;
    });
  }

  getInitialSelectedDate() {
    const today = new Date(this.today.year, this.today.month, this.today.day);
    const hasService = this.selectedService.availability.find(
      i => i.day_of_week.toLowerCase() === this.getWeekdayName(today.getDay()).toLowerCase()
    );

    if (hasService) return this.formatDateKey(today);

    const weekdayOrder = this.getWeekdayOrder();
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      const nextIndex = weekdayOrder.indexOf(this.getWeekdayName(nextDate.getDay()));
      if (this.servicesByWeekday[nextIndex].length > 0) return this.formatDateKey(nextDate);
    }

    return null;
  }

  getTimeSlotsForDate(dateKey) {
    const date = new Date(dateKey + 'T00:00:00');
    const availability = this.selectedService.availability.find(
      a => a.day_of_week.toLowerCase() === this.getWeekdayName(date.getDay())
    );
    if (!availability) return [];

    const slots = [];
    let [hour, minute] = availability.start_time.split(':').map(Number);
    const [endHour, endMinute] = availability.end_time.split(':').map(Number);
    const endTotal = endHour * 60 + endMinute;

    while ((hour * 60 + minute) + this.selectedService.booking_duration <= endTotal) {
      slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
      minute += this.selectedService.booking_duration;
      if (minute >= 60) { hour += Math.floor(minute / 60); minute = minute % 60; }
    }

    return slots;
  }

  // — Utilities —

  updateButtonStates() {
    const prevDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
    const nextDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
    this.setButtonState(this.prevButton, this.canGoToMonth(prevDate));
    this.setButtonState(this.nextButton, this.canGoToMonth(nextDate));
  }

  setButtonState(button, isActive) {
    if (!button) return;
    button.disabled = !isActive;
    button.setAttribute('aria-disabled', isActive ? 'false' : 'true');
  }

  canGoToMonth(date) {
    const minDate = new Date(this.today.year, this.today.month - this.settings.maxMonthsPast, 1);
    const maxDate = new Date(this.today.year, this.today.month + this.settings.maxMonthsFuture, 1);
    return date >= minDate && date <= maxDate;
  }

  getMonthName(monthIndex) {
    return ['january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'][monthIndex];
  }

  getWeekdayName(dayIndex) {
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayIndex];
  }

  getWeekdayOrder() {
    return this.weekdayElements.map(weekday => weekday.dataset.eventsCalendarWeekday);
  }

  getNowInTimezone() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.settings.timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).formatToParts(new Date());

    const values = {};
    parts.forEach(part => values[part.type] = part.value);

    return {
      year: Number(values.year),
      month: Number(values.month) - 1,
      day: Number(values.day)
    };
  }

  formatDateKey(date) {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }

  formatDateLabel(dateKey) {
    const date = new Date(dateKey + 'T00:00:00');
    const day = date.getDate();
    const ordinal = n => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return [
      this.settings.translations[this.getWeekdayName(date.getDay())] + ',',
      ordinal(day),
      this.settings.translations[this.getMonthName(date.getMonth())],
      date.getFullYear()
    ].join(' ');
  }
}

customElements.define('booking-calendar', BookingCalendar);